import * as THREE from 'three'
import * as FRAGS from '@thatopen/fragments'
import type { FragmentsModel } from '@thatopen/fragments'
import type * as OBC from '@thatopen/components'
import type * as OBF from '@thatopen/components-front'
import type { RaycastData, RaycastResult } from '@thatopen/fragments'
import { viewerApi } from './viewerApi'
import {
  filtrarImpactosVisiveis,
  intervaloVisivelDoRaio,
  pontoVisivel,
  primeiroImpactoVisivel,
} from '@viewer/section-box/cortesNoRaio'

// World-space picking helpers backed by Fragments' built-in raycaster.
//
// Why not THREE.Raycaster.intersectObjects on model.object?
// Fragments uses streamed/instanced geometry whose BufferAttribute
// arrays are populated lazily. THREE's raycaster reads the position
// attribute directly and throws "Cannot read properties of undefined
// (reading 'N')" when the array isn't there yet. The Fragments
// raycast API guards against this and is the supported path.

export type World = OBC.SimpleWorld<
  OBC.SimpleScene,
  OBC.SimpleCamera,
  OBF.RendererWith2D
>

interface PickResult {
  point: THREE.Vector3
  // When the pick used snap and a vertex/edge snap fired, this is the
  // snapped target point (start of the snapped edge for LINE,
  // the vertex itself for POINT). Falls back to `point` if no snap.
  snappedPoint: THREE.Vector3
  snappedEdgeP1?: THREE.Vector3
  snappedEdgeP2?: THREE.Vector3
  snappingClass?: FRAGS.SnappingClass
  modelId: string
  localId: number
  normal?: THREE.Vector3
  distance: number
}

const ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)

// ---------------------------------------------------------------------------
// Raio com cortes.
//
// O modelo já recebe os planos de corte (viewerApi.ligarCortesAoModelo)
// e o Fragments filtra os impactos no worker. Estas funções voltam a
// filtrar do lado da aplicação, porque o worker usa a ÚLTIMA vista que
// recebeu: entre mexer na caixa e a vista nova chegar lá, o raio ainda
// via os planos antigos (ou nenhum). Ver docs/CAIXA_DE_CORTE.md.
// ---------------------------------------------------------------------------

/** O raio do rato no mundo, como o Fragments o calcula (coordenadas de
 *  cliente; o rectângulo do canvas converte-as). */
export function raioDoRato(
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
  dom: HTMLElement,
  clientX: number,
  clientY: number,
): THREE.Ray {
  const rect = dom.getBoundingClientRect()
  const ndc = new THREE.Vector2(
    ((clientX - rect.left) / rect.width) * 2 - 1,
    -((clientY - rect.top) / rect.height) * 2 + 1,
  )
  const raycaster = new THREE.Raycaster()
  raycaster.setFromCamera(ndc, camera)
  return raycaster.ray
}

/** Com cortes activos, o raio debaixo do rato passa pela parte visível?
 *  Se não passa (cursor fora da caixa no ecrã, por exemplo), não há
 *  nada para apanhar ali e nem vale a pena pedir o raio ao worker. */
export function raioAtravessaParteVisivel(
  data: RaycastData,
  planos: readonly THREE.Plane[],
): boolean {
  if (planos.length === 0) return true
  const ray = raioDoRato(data.camera, data.dom, data.mouse.x, data.mouse.y)
  return intervaloVisivelDoRaio(ray.origin, ray.direction, planos) !== null
}

/** O primeiro impacto VISÍVEL no modelo.
 *
 *  Caso comum (sem cortes, ou o mais próximo já à vista): um só
 *  `raycast`, como antes. Só quando o mais próximo cai na parte cortada
 *  se pede a lista toda e se escolhe o primeiro à vista, que é o que o
 *  utilizador está de facto a ver debaixo do cursor. */
export async function raycastVisivel(
  model: FragmentsModel,
  data: RaycastData,
  planos: readonly THREE.Plane[] = viewerApi.cortesActivos(),
): Promise<RaycastResult | null> {
  if (!raioAtravessaParteVisivel(data, planos)) return null
  const r = await model.raycast(data).catch(() => null)
  if (!r) return null
  if (planos.length === 0 || pontoVisivel(r.point, planos)) return r
  const todos = await model.raycastAll(data).catch(() => null)
  return primeiroImpactoVisivel(todos, planos)
}

/** Todos os impactos no modelo, sem os que caem na parte cortada. */
export async function raycastAllVisiveis(
  model: FragmentsModel,
  data: RaycastData,
  planos: readonly THREE.Plane[] = viewerApi.cortesActivos(),
): Promise<RaycastResult[]> {
  if (!raioAtravessaParteVisivel(data, planos)) return []
  const todos = await model.raycastAll(data).catch(() => null)
  return filtrarImpactosVisiveis(todos ?? [], planos)
}

/**
 * Picks the closest model surface point under the given client
 * coordinates. Returns null if no model is loaded or the ray
 * misses everything (and no ground fallback is requested).
 */
export async function pickWorldPoint(
  world: World,
  dom: HTMLCanvasElement,
  clientX: number,
  clientY: number,
  options: {
    snap?: boolean
    fallbackToGround?: boolean
    // Restrict snap to a single class. Default = use all three.
    snapClass?: 'all' | 'point' | 'line' | 'face'
  } = {},
): Promise<PickResult | null> {
  const { snap = false, fallbackToGround = true, snapClass = 'all' } = options
  const camera = world.camera.three as
    | THREE.PerspectiveCamera
    | THREE.OrthographicCamera
  const mouse = new THREE.Vector2(clientX, clientY)

  const snappingClasses =
    snapClass === 'point'
      ? [FRAGS.SnappingClass.POINT]
      : snapClass === 'line'
        ? [FRAGS.SnappingClass.LINE]
        : snapClass === 'face'
          ? [FRAGS.SnappingClass.FACE]
          : [
              FRAGS.SnappingClass.POINT,
              FRAGS.SnappingClass.LINE,
              FRAGS.SnappingClass.FACE,
            ]

  let best: PickResult | null = null
  for (const [modelId, model] of viewerApi.iterModels()) {
    if (!model.object.visible) continue
    try {
      if (snap) {
        const results = await (model as FragmentsModel)
          .raycastWithSnapping({
            camera,
            mouse,
            dom,
            snappingClasses,
          })
          .catch(() => null)
        // Os pontos de snap que caem na parte cortada não estão à vista:
        // saem, e fica o primeiro dos que sobram, como antes.
        const visiveis = filtrarImpactosVisiveis(
          results ?? [],
          viewerApi.cortesActivos(),
        )
        if (visiveis.length === 0) continue
        const r = visiveis[0]!
        if (typeof r.localId !== 'number') continue
        const snappedPoint = r.snappedEdgeP1
          ? r.snappedEdgeP1.clone()
          : r.point.clone()
        const candidate: PickResult = {
          point: r.point.clone(),
          snappedPoint,
          snappedEdgeP1: r.snappedEdgeP1?.clone(),
          snappedEdgeP2: r.snappedEdgeP2?.clone(),
          snappingClass: r.snappingClass,
          modelId,
          localId: r.localId,
          normal: r.normal?.clone(),
          distance: r.distance,
        }
        if (!best || candidate.distance < best.distance) best = candidate
      } else {
        const r = await raycastVisivel(model as FragmentsModel, {
          camera,
          mouse,
          dom,
        })
        if (!r || typeof r.localId !== 'number') continue
        const candidate: PickResult = {
          point: r.point.clone(),
          snappedPoint: r.point.clone(),
          modelId,
          localId: r.localId,
          normal: r.normal?.clone(),
          distance: r.distance,
        }
        if (!best || candidate.distance < best.distance) best = candidate
      }
    } catch {
      // Defensive: a transient streaming state can throw inside
      // Fragments. Skip this model and try the others.
    }
  }
  if (best) return best

  if (!fallbackToGround) return null

  // Ground-plane fallback for clicks landing in empty sky.
  const rect = dom.getBoundingClientRect()
  const ndc = new THREE.Vector2(
    ((clientX - rect.left) / rect.width) * 2 - 1,
    -((clientY - rect.top) / rect.height) * 2 + 1,
  )
  const raycaster = new THREE.Raycaster()
  raycaster.setFromCamera(ndc, camera)
  const hit = new THREE.Vector3()
  if (!raycaster.ray.intersectPlane(ground, hit)) return null
  return {
    point: hit.clone(),
    snappedPoint: hit.clone(),
    modelId: '',
    localId: -1,
    distance: Number.MAX_VALUE,
  }
}
